import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import { ChangelogWidget } from '../components/ChangelogWidget';
import { getCardgames, getGameIcon } from '../services/supabaseService';

interface FAQItem {
  question: string;
  answer: string;
}

interface GameItem {
  id: number | string;
  name: string;
  subtitle: string;
  slug: string;
  symbol: string;
  badgeColor: string;
  borderColor: string;
  bgGlow: string;
}

const FAQ_DATA: FAQItem[] = [
  {
    question: 'Para quem é o Cardumy?',
    answer: 'O Cardumy foi criado para colecionadores, jogadores casuais, competidores e lojistas de TCG. Seja para organizar suas cartas em pastas com cotação em tempo real, montar e testar decks lendários, encontrar torneios oficiais ou negociar diretamente com outros jogadores e lojas verificadas.'
  },
  {
    question: 'O Cardumy é gratuito?',
    answer: 'Sim! Para os membros e jogadores, o Cardumy é 100% gratuito para sempre — organize sua coleção, monte decks e negocie sem pagar nada. Para lojistas, oferecemos um período de teste gratuito por 1 mês com todas as ferramentas de estoque, vendas e torneios, com valores posteriores a definir.'
  },
  {
    question: 'Como funcionam as trocas?',
    answer: 'Você pode definir quais cartas da sua coleção estão disponíveis para troca ou venda, adicionar cartas desejadas à sua Wishlist e o Cardumy conecta automaticamente você com outros membros que possuem os cards que você procura, com filtros por região e reputação.'
  },
  {
    question: 'Como as lojas podem participar?',
    answer: 'Lojistas verificados têm acesso a um ecossistema completo: controle de estoque sincronizado, importação em lote de cartas, gerenciamento e criação de torneios oficiais com chaveamento, e uma vitrine com pagamentos seguros.'
  },
  {
    question: 'O que é o sistema de reputação?',
    answer: 'É o índice de confiança da comunidade. A cada negociação concluída ou participação em eventos, usuários e lojas recebem avaliações e selos de verificação, garantindo um ambiente transparente, confiável e seguro para todos.'
  }
];

// Metadados visuais de todos os jogos suportados pelo Cardumy
const DEFAULT_ALL_GAMES: GameItem[] = [
  {
    id: 1,
    name: 'One Piece',
    subtitle: 'Bandai Card Games',
    slug: 'one-piece',
    symbol: 'fas fa-anchor',
    badgeColor: 'text-rose-400 group-hover:text-rose-300',
    borderColor: 'hover:border-rose-500/50',
    bgGlow: 'group-hover:bg-rose-950/20'
  },
  {
    id: 2,
    name: 'Digimon',
    subtitle: 'Bandai Card Games',
    slug: 'digimon',
    symbol: 'fas fa-dragon',
    badgeColor: 'text-sky-400 group-hover:text-sky-300',
    borderColor: 'hover:border-sky-500/50',
    bgGlow: 'group-hover:bg-sky-950/20'
  },
  {
    id: 3,
    name: 'Pokémon',
    subtitle: 'The Pokémon Company',
    slug: 'pokemon',
    symbol: 'fas fa-bolt',
    badgeColor: 'text-yellow-400 group-hover:text-yellow-300',
    borderColor: 'hover:border-yellow-500/50',
    bgGlow: 'group-hover:bg-yellow-950/20'
  },
  {
    id: 4,
    name: 'Magic: The Gathering',
    subtitle: 'Wizards of the Coast',
    slug: 'magic',
    symbol: 'fas fa-wand-magic-sparkles',
    badgeColor: 'text-amber-400 group-hover:text-amber-300',
    borderColor: 'hover:border-amber-500/50',
    bgGlow: 'group-hover:bg-amber-950/20'
  },
  {
    id: 5,
    name: 'Yu-Gi-Oh!',
    subtitle: 'Konami Official',
    slug: 'yugioh',
    symbol: 'fas fa-eye',
    badgeColor: 'text-purple-400 group-hover:text-purple-300',
    borderColor: 'hover:border-purple-500/50',
    bgGlow: 'group-hover:bg-purple-950/20'
  },
  {
    id: 6,
    name: 'Dragon Ball Super',
    subtitle: 'Fusion World & Masters',
    slug: 'dragon-ball-fusion',
    symbol: 'fas fa-fire',
    badgeColor: 'text-orange-400 group-hover:text-orange-300',
    borderColor: 'hover:border-orange-500/50',
    bgGlow: 'group-hover:bg-orange-950/20'
  },
  {
    id: 7,
    name: 'Gundam',
    subtitle: 'Bandai Official',
    slug: 'gundam',
    symbol: 'fas fa-robot',
    badgeColor: 'text-blue-400 group-hover:text-blue-300',
    borderColor: 'hover:border-blue-500/50',
    bgGlow: 'group-hover:bg-blue-950/20'
  },
  {
    id: 14,
    name: 'Disney Lorcana',
    subtitle: 'Ravensburger TCG',
    slug: 'lorcana',
    symbol: 'fas fa-wand-sparkles',
    badgeColor: 'text-teal-400 group-hover:text-teal-300',
    borderColor: 'hover:border-teal-500/50',
    bgGlow: 'group-hover:bg-teal-950/20'
  },
  {
    id: 9,
    name: 'Union Arena',
    subtitle: 'Bandai All-Stars',
    slug: 'union-arena',
    symbol: 'fas fa-layer-group',
    badgeColor: 'text-pink-400 group-hover:text-pink-300',
    borderColor: 'hover:border-pink-500/50',
    bgGlow: 'group-hover:bg-pink-950/20'
  },
  {
    id: 10,
    name: 'Star Wars',
    subtitle: 'Unlimited TCG',
    slug: 'star-wars',
    symbol: 'fas fa-jedi',
    badgeColor: 'text-amber-300 group-hover:text-amber-200',
    borderColor: 'hover:border-amber-400/50',
    bgGlow: 'group-hover:bg-amber-950/20'
  },
  {
    id: 11,
    name: 'Vanguard',
    subtitle: 'Bushiroad Official',
    slug: 'cardfight-vanguard',
    symbol: 'fas fa-shield-halved',
    badgeColor: 'text-cyan-400 group-hover:text-cyan-300',
    borderColor: 'hover:border-cyan-500/50',
    bgGlow: 'group-hover:bg-cyan-950/20'
  },
  {
    id: 12,
    name: 'Flesh and Blood',
    subtitle: 'Legend Story Studios',
    slug: 'fab',
    symbol: 'fas fa-hand-fist',
    badgeColor: 'text-red-400 group-hover:text-red-300',
    borderColor: 'hover:border-red-500/50',
    bgGlow: 'group-hover:bg-red-950/20'
  },
  {
    id: 13,
    name: 'Sorcery',
    subtitle: 'Contested Realm',
    slug: 'sorcery',
    symbol: 'fas fa-hat-wizard',
    badgeColor: 'text-emerald-400 group-hover:text-emerald-300',
    borderColor: 'hover:border-emerald-500/50',
    bgGlow: 'group-hover:bg-emerald-950/20'
  },
  {
    id: 'riftbound',
    name: 'Riftbound',
    subtitle: 'Riftbound TCG',
    slug: 'riftbound',
    symbol: 'fas fa-gem',
    badgeColor: 'text-indigo-400 group-hover:text-indigo-300',
    borderColor: 'hover:border-indigo-500/50',
    bgGlow: 'group-hover:bg-indigo-950/20'
  },
  {
    id: 15,
    name: 'UniVersus',
    subtitle: 'UVS Games',
    slug: 'universus',
    symbol: 'fas fa-burst',
    badgeColor: 'text-violet-400 group-hover:text-violet-300',
    borderColor: 'hover:border-violet-500/50',
    bgGlow: 'group-hover:bg-violet-950/20'
  },
  {
    id: 16,
    name: 'Hololive OCG',
    subtitle: 'Cover Corp / Bushiroad',
    slug: 'hololive',
    symbol: 'fas fa-star',
    badgeColor: 'text-cyan-300 group-hover:text-cyan-200',
    borderColor: 'hover:border-cyan-400/50',
    bgGlow: 'group-hover:bg-cyan-950/20'
  },
  {
    id: 17,
    name: 'Altered',
    subtitle: 'Equinox Official',
    slug: 'altered',
    symbol: 'fas fa-compass',
    badgeColor: 'text-lime-400 group-hover:text-lime-300',
    borderColor: 'hover:border-lime-500/50',
    bgGlow: 'group-hover:bg-lime-950/20'
  },
  {
    id: 18,
    name: 'Grand Archive',
    subtitle: 'Weebs of the Shore',
    slug: 'grand-archive',
    symbol: 'fas fa-book-atlas',
    badgeColor: 'text-fuchsia-400 group-hover:text-fuchsia-300',
    borderColor: 'hover:border-fuchsia-500/50',
    bgGlow: 'group-hover:bg-fuchsia-950/20'
  },
  {
    id: 20,
    name: 'Godzilla',
    subtitle: 'Toho Official',
    slug: 'godzilla',
    symbol: 'fas fa-radiation',
    badgeColor: 'text-emerald-500 group-hover:text-emerald-400',
    borderColor: 'hover:border-emerald-500/50',
    bgGlow: 'group-hover:bg-emerald-950/20'
  }
];

const FEATURES_DATA = [
  {
    icon: 'fa-magnifying-glass',
    title: 'Busca de Cartas',
    desc: 'Explore cartas de diversos TCGs em segundos. Filtros inteligentes por coleção, raridade e cor.'
  },
  {
    icon: 'fa-layer-group',
    title: 'Coleção Pessoal',
    desc: 'Organize sua coleção digital e acompanhe valores e raridades com facilidade numa interface clara.'
  },
  {
    icon: 'fa-handshake',
    title: 'Negócio Fechado',
    desc: 'Salve suas wishlists e encontre jogadores prontos para negociar. Ative alertas de disponibilidade.'
  },
  {
    icon: 'fa-circle-user',
    title: 'Perfis e Reputação',
    desc: 'Crie seu perfil, mostre conquistas e ganhe destaque no cardume. Sistema de níveis e confiança.'
  },
  {
    icon: 'fa-trophy',
    title: 'Rankings Globais',
    desc: 'Veja quem são os maiores colecionadores e os decks mais quentes da temporada atual.'
  },
  {
    icon: 'fa-location-dot',
    title: 'Lojas e Torneios',
    desc: 'Descubra lojas locais, eventos oficiais e fortaleça o cardume na sua região.'
  }
];

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [gameSearchQuery, setGameSearchQuery] = useState('');
  const [gamesList, setGamesList] = useState<GameItem[]>(DEFAULT_ALL_GAMES);

  useEffect(() => {
    let isMounted = true;
    getCardgames()
      .then((dbGames) => {
        if (!isMounted || !Array.isArray(dbGames) || dbGames.length === 0) return;

        // Mapear dados visuais padrão para lookup rápido
        const defaultMap = new Map<string, GameItem>();
        DEFAULT_ALL_GAMES.forEach((g) => {
          defaultMap.set(g.slug, g);
          defaultMap.set(g.name.toLowerCase(), g);
        });

        const mergedList: GameItem[] = [];
        const seenKeys = new Set<string>();

        // 1. Processar jogos que vêm do banco de dados (com ID real de consulta)
        dbGames.forEach((dbg: any) => {
          const slug = dbg.slug || String(dbg.name).toLowerCase().replace(/\s+/g, '-');
          const lookup = defaultMap.get(slug) || defaultMap.get((dbg.name || '').toLowerCase());
          const iconClass = getGameIcon(dbg.name || dbg.id);

          const uniqueKey = slug;
          if (seenKeys.has(uniqueKey)) return;
          seenKeys.add(uniqueKey);

          mergedList.push({
            id: dbg.id,
            name: dbg.name || lookup?.name || 'TCG',
            subtitle: lookup?.subtitle || 'TCG Oficial',
            slug,
            symbol: lookup?.symbol || `fas ${iconClass}`,
            badgeColor: lookup?.badgeColor || 'text-purple-400 group-hover:text-purple-300',
            borderColor: lookup?.borderColor || 'hover:border-purple-500/50',
            bgGlow: lookup?.bgGlow || 'group-hover:bg-purple-950/20'
          });
        });

        // 2. Adicionar jogos da lista padrão que não estiverem ainda cadastrados no banco
        DEFAULT_ALL_GAMES.forEach((defGame) => {
          const uniqueKey = defGame.slug;
          if (!seenKeys.has(uniqueKey)) {
            seenKeys.add(uniqueKey);
            mergedList.push(defGame);
          }
        });

        setGamesList(mergedList);
      })
      .catch((err) => {
        console.error('Erro ao carregar jogos na landing page:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(prev => prev === index ? null : index);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleGameClick = (game: GameItem) => {
    if (game.id !== undefined && game.id !== null) {
      localStorage.setItem('cardumy_active_game', String(game.id));
      navigate(`/busca?game=${game.id}`);
    } else {
      navigate('/busca');
    }
  };

  const displayedGames = useMemo(() => {
    if (!gameSearchQuery.trim()) return gamesList;
    const q = gameSearchQuery.toLowerCase().trim();
    return gamesList.filter(
      (g) => g.name.toLowerCase().includes(q) || g.subtitle.toLowerCase().includes(q)
    );
  }, [gamesList, gameSearchQuery]);

  return (
    <div className="min-h-screen bg-[#050811] text-white selection:bg-purple-500/30 relative overflow-x-hidden font-sans">
      {/* Lightweight subtle top gradient background without heavy blur filters */}
      <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-sky-950/20 via-purple-950/10 to-transparent pointer-events-none" />

      {/* Main Content */}
      <div className="relative z-10">
        
        {/* Top Floating Navbar - Solid background for max scroll performance without GPU */}
        <header className="sticky top-0 z-50 bg-[#050811] border-b border-slate-800 px-4 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 via-indigo-600 to-sky-500 flex items-center justify-center shadow-md group-hover:opacity-90 transition-opacity">
                <i className="fas fa-fish-fins text-white text-base"></i>
              </div>
              <span className="font-black text-lg tracking-tight text-white group-hover:text-purple-300 transition-colors">Cardumy</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-6 text-xs font-bold text-slate-300">
            <button onClick={() => scrollToSection('inicio')} className="hover:text-white transition-colors cursor-pointer">
              Início
            </button>
            <button onClick={() => scrollToSection('jogos')} className="hover:text-white transition-colors cursor-pointer">
              Jogos
            </button>
            <button onClick={() => scrollToSection('recursos')} className="hover:text-white transition-colors cursor-pointer">
              Recursos
            </button>
            <button onClick={() => scrollToSection('planos')} className="hover:text-white transition-colors cursor-pointer">
              Planos
            </button>
            <button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors cursor-pointer">
              FAQ
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-xs font-bold text-slate-300 hover:text-white transition-colors px-3 py-1.5"
            >
              Entrar
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-colors"
            >
              Criar Conta
            </Link>
          </div>
        </header>

        {/* =========================================================================
            1. HERO SECTION
        ========================================================================= */}
        <section id="inicio" className="pt-10 sm:pt-14 pb-8 sm:pb-10 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">
          
          {/* Badge: NADE COM O CARDUME */}
          <div className="flex justify-center mb-5 sm:mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0a1224] border border-sky-500/30 shadow-md">
              <i className="fas fa-water text-sky-400 text-xs"></i>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-sky-300">
                Nade com o Cardume
              </span>
            </div>
          </div>

          {/* Main Title: Mergulhe no oceano dos Card Games */}
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight leading-[1.08] sm:leading-[1.1] mb-5">
            Mergulhe no oceano
            <br />
            <span className="text-sky-400">dos </span>
            <span className="text-purple-400">Card </span>
            <span className="text-pink-500">Games</span>
          </h1>

          {/* Subtitle */}
          <p className="text-slate-300/90 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-medium mb-7 sm:mb-8">
            O <strong className="text-white font-bold">Cardumy</strong> conecta colecionadores como um grande cardume.
            <br className="hidden sm:block" />
            Organize sua coleção, acompanhe tendências e encontre seu lugar nas profundezas do TCG.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-7">
            <Link
              to="/dashboard"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-slate-950 font-black text-xs sm:text-sm rounded-full hover:bg-slate-100 transition-colors shadow-lg flex items-center justify-center gap-2 group cursor-pointer"
            >
              <span>Começar agora</span>
              <i className="fas fa-arrow-right text-xs group-hover:translate-x-0.5 transition-transform"></i>
            </Link>

            <button
              onClick={() => scrollToSection('jogos')}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#0b1020] text-slate-200 font-bold text-xs sm:text-sm rounded-full border border-slate-700/80 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <span>Ver Todos os Jogos</span>
              <i className="fas fa-layer-group text-xs text-purple-400"></i>
            </button>
          </div>

          {/* Social Icons */}
          <div className="flex items-center justify-center gap-6 text-slate-400">
            <a
              href="https://www.instagram.com/cardumy/"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram Cardumy"
              className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-pink-400 hover:border-pink-500/40 transition-colors"
            >
              <i className="fab fa-instagram text-sm"></i>
            </a>
            <a
              href="https://chat.whatsapp.com/CdX5OCXmlojHTutlbjEqId"
              target="_blank"
              rel="noreferrer"
              aria-label="Comunidade WhatsApp Cardumy"
              className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40 transition-colors"
            >
              <i className="fab fa-whatsapp text-sm"></i>
            </a>
            <a
              href="https://discord.gg/hNWvmdz6ja"
              target="_blank"
              rel="noreferrer"
              aria-label="Discord Oficial Cardumy"
              className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-indigo-400 hover:border-indigo-500/40 transition-colors"
            >
              <i className="fab fa-discord text-sm"></i>
            </a>
          </div>
        </section>

        {/* =========================================================================
            3. SECTION: RECURSOS DO CARDUME
        ========================================================================= */}
        <section id="recursos" className="py-10 sm:py-14 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-2">
              Recursos do Cardume
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 font-medium">
              Mantenha sua rede conectada e sua coleção sempre em alta.
            </p>
          </div>

          {/* 6 Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {FEATURES_DATA.map((feat, i) => (
              <div
                key={i}
                className="p-6 sm:p-7 rounded-2xl sm:rounded-3xl bg-[#090e1c] border border-slate-800 hover:border-slate-700 hover:bg-slate-900/60 transition-colors group relative overflow-hidden"
              >
                {/* Icon Box with Pink/Purple Border Accent */}
                <div className="w-11 h-11 rounded-xl bg-purple-950/40 border border-pink-500/30 flex items-center justify-center mb-5 text-pink-400 group-hover:border-pink-500/60 transition-colors shadow-sm">
                  <i className={`fas ${feat.icon} text-sm sm:text-base`}></i>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-white mb-2 tracking-tight group-hover:text-purple-300 transition-colors">
                  {feat.title}
                </h3>
                
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed font-normal">
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>

        </section>

        {/* =========================================================================
            2. SECTION: TODOS OS NOSSOS JOGOS
        ========================================================================= */}
        <section id="jogos" className="py-12 sm:py-16 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            {/* Header */}
            <div className="mb-8 sm:mb-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#0a1224] border border-purple-500/30 shadow-sm mb-3">
                <i className="fas fa-layer-group text-purple-400 text-xs"></i>
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-purple-300">
                  Ecossistema Multi-TCG
                </span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight mb-2.5">
                Todos os Nossos Jogos
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 font-medium leading-relaxed">
                Mergulhe nas cartas, coleções, cotações de mercado e montagem de decks para as maiores franquias de Card Games do mundo.
              </p>

            </div>

            {/* High-Performance, Complete Responsive Game Cards Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3 sm:gap-4">
              {displayedGames.map((game) => (
                <button
                  key={`game-landing-${game.slug}`}
                  onClick={() => handleGameClick(game)}
                  className={`group relative flex flex-col items-center justify-between p-3 sm:p-4 rounded-2xl bg-[#090e1c] border border-slate-800 ${game.borderColor} hover:bg-slate-900/90 transition-all duration-200 text-center cursor-pointer shadow-md hover:scale-[1.02]`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-slate-800/80 border border-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform ${game.bgGlow}`}>
                    <i className={`${game.symbol} ${game.badgeColor} text-lg sm:text-xl transition-colors`}></i>
                  </div>
                  
                  <div className="w-full">
                    <span className="text-xs font-bold text-white group-hover:text-purple-200 transition-colors line-clamp-1 leading-snug">
                      {game.name}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium mt-0.5 line-clamp-1 block">
                      {game.subtitle}
                    </span>
                  </div>

                  <div className="mt-3 w-full pt-2 border-t border-slate-800/60 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[9px] font-black uppercase tracking-wider text-purple-400">Ver Cartas</span>
                    <i className="fas fa-chevron-right text-[8px] text-purple-400"></i>
                  </div>
                </button>
              ))}
            </div>

            {displayedGames.length === 0 && (
              <div className="py-12 text-center bg-[#090e1c] border border-slate-800 rounded-2xl p-6 max-w-md mx-auto">
                <i className="fas fa-ghost text-slate-600 text-3xl mb-3"></i>
                <p className="text-sm font-bold text-slate-300">Nenhum jogo encontrado com "{gameSearchQuery}".</p>
                <button
                  onClick={() => setGameSearchQuery('')}
                  className="mt-3 px-4 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                >
                  Mostrar todos os jogos
                </button>
              </div>
            )}

          </div>
        </section>

        {/* =========================================================================
            3.5. SECTION: PLANOS & ACESSO
        ========================================================================= */}
        <section id="planos" className="py-12 sm:py-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          <div className="text-center mb-10 sm:mb-12 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#0a1224] border border-purple-500/30 shadow-sm mb-3">
              <i className="fas fa-gem text-purple-400 text-xs"></i>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-purple-300">
                Planos & Acesso
              </span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight mb-2.5">
              Escolha seu lugar no Cardume
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 font-medium leading-relaxed">
              Recursos completos e pensados para quem vive o mundo dos Card Games. Sem pegadinhas e com total transparência.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
            
            {/* PLANO 1: MEMBRO */}
            <div className="rounded-3xl bg-[#090e1c] border border-slate-800 hover:border-slate-700/80 p-6 sm:p-8 flex flex-col justify-between transition-all duration-200 shadow-xl relative overflow-hidden group">
              <div className="space-y-6">
                {/* Header do Card */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-400">
                      Jogadores & Colecionadores
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                      100% Grátis
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                    Membro
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 font-normal leading-relaxed mt-2">
                    Tudo o que você precisa para gerenciar suas cartas, montar decks competitivos e negociar no cardume.
                  </p>
                </div>

                {/* Bloco de Preço */}
                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 flex items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                    R$ 0
                  </span>
                  <span className="text-xs font-bold text-slate-400">
                    / para sempre
                  </span>
                </div>

                {/* Lista de Recursos */}
                <div className="space-y-3 pt-2">
                  <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                    Recursos incluídos para Membros:
                  </p>
                  <ul className="space-y-2.5">
                    {[
                      'Acesso completo ao catálogo de cartas de mais de 20 TCGs',
                      'Pastas de coleção ilimitadas com cotação de mercado em tempo real',
                      'Deckbuilder avançado com importação e exportação de listas de decks',
                      'Análise de Metagame competitivo com arquétipos dominantes e estatísticas',
                      'Diário de Torneios com registro detalhado rodada a rodada e relatórios',
                      'Sistema de Trocas com Wishlist inteligente e matchmaking comunitário',
                      'Inscrição em torneios e acompanhamento de chaves e colocações',
                      'Perfil personalizado com histórico, conquistas e reputação confiável',
                      'Filtros de busca avançada por coleção, raridade, cor e atributos'
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-300 leading-snug">
                        <i className="fas fa-check text-emerald-400 text-[11px] mt-0.5 shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Ação e Rodapé do Card */}
              <div className="pt-6 sm:pt-8 mt-6 border-t border-slate-800/80 space-y-2.5">
                <Link
                  to="/login"
                  className="w-full py-3.5 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-slate-800 text-white font-bold text-xs sm:text-sm transition-all text-center border border-slate-700/80 hover:border-slate-600 block shadow-md cursor-pointer"
                >
                  Criar Conta Gratuita
                </Link>
                <p className="text-[11px] text-slate-500 text-center font-medium">
                  100% gratuito · Sem necessidade de cartão de crédito
                </p>
              </div>
            </div>

            {/* PLANO 2: LOJISTA */}
            <div className="rounded-3xl bg-gradient-to-b from-[#0f1429] via-[#090e1c] to-[#090e1c] border-2 border-purple-500/50 hover:border-purple-500 p-6 sm:p-8 flex flex-col justify-between transition-all duration-200 shadow-2xl shadow-purple-950/30 relative overflow-hidden group">
              {/* Glow sutil de topo */}
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-purple-600/15 rounded-full blur-3xl pointer-events-none" />

              <div className="space-y-6 relative z-10">
                {/* Header do Card */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-xs font-black uppercase tracking-wider text-purple-300">
                      Lojas Físicas & Virtuais
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm shadow-purple-500/30">
                      1 Mês Grátis
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                    <span>Lojista</span>
                    <i className="fas fa-store text-purple-400 text-lg"></i>
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-300 font-normal leading-relaxed mt-2">
                    Transforme sua loja no polo oficial da sua região com ferramentas completas de estoque, vendas e torneios.
                  </p>
                </div>

                {/* Bloco de Preço */}
                <div className="p-4 rounded-2xl bg-purple-950/30 border border-purple-500/30 flex flex-col gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                      30 Dias Grátis
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-purple-300/90">
                    Período de teste gratuito por 1 mês · Preço posterior a definir
                  </p>
                </div>

                {/* Lista de Recursos */}
                <div className="space-y-3 pt-2">
                  <p className="text-[11px] font-black uppercase tracking-wider text-purple-400">
                    Tudo o que a sua loja precisa:
                  </p>
                  <ul className="space-y-2.5">
                    {[
                      '1 mês de teste 100% gratuito para conhecer sem compromisso',
                      'Vitrine pública dedicada e página exclusiva de Loja Verificada no Cardumy',
                      'Gestão de estoque integrada com importação em lote de cartas e produtos',
                      'Venda direta de boosters e produtos selados para toda a rede',
                      'Criação e administração completa de torneios',
                      'Módulo de Pedido Balcão para atendimento rápido em loja física',
                      'Gestão de pedidos recebidos, envios, balcão e histórico de vendas',
                      'Configuração de pagamentos integrados e fretes personalizados',
                      'Controle de saldo, créditos de clientes e programa de fidelidade da loja',
                      'Selo oficial de Loja Verificada com reputação em destaque'
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-200 leading-snug">
                        <i className="fas fa-circle-check text-purple-400 text-[11px] mt-0.5 shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Ação e Rodapé do Card */}
              <div className="pt-6 sm:pt-8 mt-6 border-t border-purple-500/20 space-y-2.5 relative z-10">
                <Link
                  to="/login"
                  className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 active:scale-[0.99] text-white font-black text-xs sm:text-sm uppercase tracking-wider transition-all text-center block shadow-lg shadow-purple-600/30 cursor-pointer"
                >
                  Experimentar 30 Dias Grátis
                </Link>
                <p className="text-[11px] text-purple-300/80 text-center font-medium">
                  Ativação imediata · Condições e mensalidade pós-teste a definir
                </p>
              </div>
            </div>

          </div>

        </section>

        {/* =========================================================================
            4. SECTION: DÚVIDAS FREQUENTES (FAQ)
        ========================================================================= */}
        <section id="faq" className="py-10 sm:py-14 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-7 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-2">
              Dúvidas Frequentes
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 font-medium">
              Tudo que você precisa saber sobre como entrar no cardume.
            </p>
          </div>

          {/* Accordion Items */}
          <div className="space-y-3">
            {FAQ_DATA.map((item, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl bg-[#090e1c] border border-slate-800 overflow-hidden transition-colors hover:border-slate-700"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left focus:outline-none cursor-pointer group"
                  >
                    <span className="text-xs sm:text-sm font-bold text-slate-200 group-hover:text-white transition-colors pr-4">
                      {item.question}
                    </span>
                    <div
                      className={`text-slate-400 group-hover:text-purple-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    >
                      <i className="fas fa-chevron-down text-xs"></i>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-4 pt-1 text-xs sm:text-sm text-slate-400 leading-relaxed border-t border-slate-800/40">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </section>


        {/* =========================================================================
            5. FOOTER
        ========================================================================= */}
        <footer className="pt-6 pb-10 border-t border-slate-800/30 text-center px-4 max-w-4xl mx-auto">
          {/* Center Logo */}
          <div className="flex items-center justify-center gap-2.5 mb-4">
            <i className="fas fa-fish-fins text-slate-500 text-sm"></i>
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Cardumy</span>
          </div>

          <p className="text-[11px] text-slate-500 font-medium mb-3">
            © 2025 Cardumy - Todos os direitos reservados.
          </p>

          <div className="flex items-center justify-center gap-4 text-xs font-medium text-slate-400 mb-6">
            <Link to="/termos" className="hover:text-purple-300 transition-colors">Termos e Condições</Link>
            <span className="text-slate-700">•</span>
            <Link to="/suporte" className="hover:text-purple-300 transition-colors">Suporte</Link>
          </div>

          {/* Legal Disclaimer */}
          <p className="text-[9px] text-slate-600 leading-relaxed uppercase tracking-wider max-w-2xl mx-auto">
            As informações literais e gráficas apresentadas sobre qualquer jogo são marcas registradas de seus respectivos proprietários. Este site não é afiliado nem endossado por eles.
          </p>
        </footer>

      </div>
    </div>
  );
};

